import { useTranslation } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { isEventMoreThanNDaysAway } from '@shared/utils/dateHelpers';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { Maybe } from '@graphql/generated-base';

const HOTEL_AVAILABILITY_THRESHOLD_DAYS = 180;

type SoldOutMessageResult = Readonly<{
  message: string;
  tooltip: string | null;
  isRatesNotReleased: boolean;
}>;

export const useSoldOutMessage = (eventDate?: Maybe<Date>): SoldOutMessageResult => {
  const { tq } = useTranslation('sharedComponents');
  const { finalizedEventDate } = useEventInfo();
  const [isExtendedStayDatesPricingEnabled] = useIsFeatureEnabled('extendedStayDatesPricing');

  const usesSearchedDate = !!eventDate;
  const isMoreThanThresholdDays = isEventMoreThanNDaysAway(eventDate ?? finalizedEventDate ?? null, HOTEL_AVAILABILITY_THRESHOLD_DAYS);

  if (isMoreThanThresholdDays) {
    return {
      message: usesSearchedDate ? tq`Rooms haven't been released for these dates yet.` : tq`Rooms haven't been released for the event dates yet.`,
      tooltip: tq`Hotels can open bookings anywhere from 5 to 12 months in advance. We'll show prices here as soon as availability opens. Check back as your date gets closer.`,
      isRatesNotReleased: true,
    };
  }

  return {
    message: tq`No rooms are available for these dates.`,
    tooltip: isExtendedStayDatesPricingEnabled ? tq`This hotel may have minimum stay requirements. Try expanding your dates to see availability.` : null,
    isRatesNotReleased: false,
  };
};
